import { AlertTriangle } from 'lucide-react';
import type { CreatedClientSecret } from '@baex/shared';
import { CopyButton } from '../../components/ui/CopyButton';

/**
 * The one-time key display (see the "Create a new client" section, step 2, reused by the "Rotate key" section).
 * The raw key is never stored server-side, so this panel genuinely cannot be shown again.
 */
export function SecretPanel({ secret }: { secret: CreatedClientSecret }) {
  return (
    <div className="rounded border border-warning bg-warning-soft p-3">
      <p className="flex items-start gap-2 text-[13px] font-semibold text-ink">
        <AlertTriangle size={15} className="mt-0.5 shrink-0 text-warning" aria-hidden="true" />
        <span>
          This key is shown ONLY ONCE.
          <span className="block font-normal">
            Copy it now — it cannot be retrieved later. If it is lost, rotate the key to generate a
            fresh one.
          </span>
        </span>
      </p>

      <div className="mt-3 flex flex-col gap-3">
        <div>
          <p className="text-2xs uppercase tracking-wide text-ink-muted">API key</p>
          <div className="mt-1 flex items-start gap-2">
            <code className="tabular min-w-0 flex-1 break-all rounded border border-hairline-strong bg-surface px-2 py-1.5 text-[12px] text-ink">
              {secret.apiKey}
            </code>
            <CopyButton value={secret.apiKey} label="Copy API key" />
          </div>
        </div>

        <div>
          <p className="text-2xs uppercase tracking-wide text-ink-muted">
            Authorization header value to give the client
          </p>
          <div className="mt-1 flex items-start gap-2">
            <code className="tabular min-w-0 flex-1 break-all rounded border border-hairline-strong bg-surface px-2 py-1.5 text-[12px] text-ink">
              {secret.authorizationHeader}
            </code>
            <CopyButton
              value={secret.authorizationHeader}
              label="Copy Authorization header value"
            />
          </div>
        </div>
      </div>

      <p className="mt-3 text-2xs text-ink-muted">
        Send it to the integration owner over a secure channel — not plain email or chat.
      </p>
    </div>
  );
}
